import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { Trophy, Repeat, Star, Crown, Medal } from "lucide-react";

const rankColor = (i) => (i === 0 ? "bg-[#E6B31E] text-black" : i === 1 ? "bg-[#C0C0C0] text-black" : i === 2 ? "bg-[#CD7F32] text-white" : "bg-black text-[#E6B31E]");

function Row({ e, i, metric }) {
  return (
    <Link to={`/user/${e.user_id}`} data-testid={`lb-row-${e.user_id}`} className="flex items-center gap-4 bg-white nb-border rounded-2xl p-4 nb-shadow nb-hover">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-lg border-2 border-black ${rankColor(i)}`}>
        {i < 3 ? <Medal size={20} strokeWidth={2.5} /> : i + 1}
      </div>
      {e.picture ? (
        <img src={e.picture} alt="" className="w-11 h-11 rounded-full nb-border object-cover" />
      ) : (
        <div className="w-11 h-11 rounded-full bg-black text-[#E6B31E] nb-border flex items-center justify-center font-extrabold">{e.name?.[0]?.toUpperCase()}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-display font-extrabold text-lg truncate flex items-center gap-2">
          {e.name}
          {e.is_member && <Crown size={14} className="text-[#B8860B]" strokeWidth={3} />}
        </div>
        {metric === "quality" ? (
          <div className="flex items-center gap-1.5"><Stars value={e.rating} size={13} /><span className="text-xs font-semibold text-black/50">({e.review_count})</span></div>
        ) : (
          <div className="text-xs font-semibold text-black/50">{e.completed_trades} trade{e.completed_trades === 1 ? "" : "s"} completed</div>
        )}
      </div>
      <div className="text-right">
        {metric === "quality" ? (
          <div className="font-display font-black text-2xl">{e.rating || "—"}</div>
        ) : (
          <div className="font-display font-black text-2xl">{e.completed_trades}</div>
        )}
      </div>
    </Link>
  );
}

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("trades");

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setData(data)).catch(() => setData({ top_trades: [], top_quality: [] }));
  }, []);

  if (!data) return <div className="py-20 text-center font-display font-extrabold text-xl animate-pulse">Loading leaderboard…</div>;

  const list = tab === "trades" ? data.top_trades : data.top_quality;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Trophy className="text-[#B8860B]" size={30} strokeWidth={2.5} />
        <h1 className="font-display font-black text-4xl tracking-tighter">Leaderboard</h1>
      </div>
      <p className="text-black/50 font-medium mb-8">The exchange's top traders — by jobs completed and by quality of goods & services.</p>

      <div className="flex gap-2 mb-8">
        <button data-testid="lb-tab-trades" onClick={() => setTab("trades")}
          className={`flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 border-2 border-black transition-colors ${tab === "trades" ? "bg-black text-[#E6B31E]" : "bg-white hover:bg-[#FBF7E9]"}`}>
          <Repeat size={16} strokeWidth={3} /> Most jobs completed
        </button>
        <button data-testid="lb-tab-quality" onClick={() => setTab("quality")}
          className={`flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 border-2 border-black transition-colors ${tab === "quality" ? "bg-[#E6B31E] text-black" : "bg-white hover:bg-[#FBF7E9]"}`}>
          <Star size={16} strokeWidth={3} /> Top quality
        </button>
      </div>

      {list.length === 0 ? (
        <div className="py-16 text-center font-display font-extrabold text-xl text-black/40">No ranked traders yet — complete trades to climb the board!</div>
      ) : (
        <div className="space-y-3">
          {list.map((e, i) => <Row key={e.user_id} e={e} i={i} metric={tab} />)}
        </div>
      )}
    </div>
  );
}
